import {
  Radio,
  ShieldCheck,
  Sigma,
  ArrowRight,
  CalendarRange,
  Building2,
  BrainCircuit,
  MessageSquare,
  Wand2,
  AlertTriangle,
  Camera,
  TrendingUp,
  Mail,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

export default function HowItWorks() {
  return (
    <div data-testid="page-how-it-works">
      <PageHeader
        title="How HQ Signal works"
        subtitle="Anonymous presence sensors, transparent metrics, no surveillance."
        testId="how-it-works-header"
      />

      {/* Hero */}
      <div
        className="relative mb-10 overflow-hidden rounded-lg border border-slate-200 shadow-sm"
        data-testid="hiw-hero"
      >
        <img
          src="https://images.unsplash.com/photo-1760224255774-66b3ab88bd83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNvcnBvcmF0ZSUyMGJsdWUlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NzQ0NTAyOXww&ixlib=rb-4.1.0&q=85"
          alt="Abstract corporate background"
          className="h-56 w-full object-cover sm:h-72"
        />
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="absolute inset-0 flex flex-col justify-center px-8 text-white sm:px-12">
          <span className="text-xs font-medium uppercase tracking-widest text-blue-200">
            Measurement methodology
          </span>
          <h2 className="mt-2 max-w-2xl font-heading text-2xl font-semibold sm:text-3xl">
            Anonymous, aggregated, actionable.
          </h2>
          <p className="mt-2 max-w-xl text-sm text-slate-200">
            HQ Signal turns presence signals into space decisions — without
            tracking individuals.
          </p>
        </div>
      </div>

      {/* Three pillars */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <Pillar
          icon={Radio}
          title="Sensors detect presence"
          body="Low-resolution PIR + thermal sensors register occupancy at desks and rooms. They never capture identity, faces, or audio."
          testId="pillar-sensors"
        />
        <Pillar
          icon={ShieldCheck}
          title="Data is anonymized"
          body="Each signal is aggregated by zone before storage. No personal device IDs, no badges, no MAC addresses involved."
          testId="pillar-privacy"
        />
        <Pillar
          icon={Sigma}
          title="Metrics, in plain English"
          body="Three formulas drive every KPI you see in this product. Hover any number to trace it back to the formula."
          testId="pillar-metrics"
        />
      </div>

      {/* Formulas */}
      <Card className="mt-10 border-slate-200 shadow-sm" data-testid="formulas-card">
        <CardContent className="grid grid-cols-1 gap-px overflow-hidden bg-slate-100 p-0 sm:grid-cols-3">
          <Formula
            label="Occupancy rate"
            formula="occupied time / total time"
            description="Fraction of working hours a desk or room is detected as occupied."
            icon={Building2}
          />
          <Formula
            label="Meeting efficiency"
            formula="avg occupancy / capacity"
            description="Average headcount in the room divided by the seats it provides."
            icon={CalendarRange}
          />
          <Formula
            label="No-show rate"
            formula="bookings without presence / total bookings"
            description="Bookings where no one was detected within the no-show delay."
            icon={ArrowRight}
          />
        </CardContent>
      </Card>

      {/* KPI deep-dive — worked examples */}
      <div className="mt-12" data-testid="kpi-deep-dive">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
              How each KPI is calculated
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Worked examples on real numbers from this dashboard.
            </p>
          </div>
          <Badge variant="secondary" className="bg-slate-100 text-slate-700">
            Methodology
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <KPIExplainer
            number="01"
            title="Desk occupancy rate"
            formula="Σ (minutes with detected presence on a desk) / Σ (working minutes per desk)"
            example="A floor has 100 desks × 10h working day = 60,000 working minutes. If the sensors register 25,380 minutes of presence across all desks, the floor occupancy rate is 25,380 / 60,000 = 42.3%."
            note="Aggregated daily by default. The Settings 'Occupancy threshold' filters out short visits below 5/10/15 minutes."
            color="blue"
          />
          <KPIExplainer
            number="02"
            title="Meeting room efficiency"
            formula="avg participants per booking / room capacity"
            example="Room Alpha has 10 seats. Across the last 60 days, the average headcount when a meeting happens is 3 people. Efficiency = 3 / 10 = 30%."
            note="Counted only for meetings that actually happened (i.e. excluding no-shows). Helps spot oversized rooms."
            color="emerald"
          />
          <KPIExplainer
            number="03"
            title="No-show rate"
            formula="bookings with zero presence within delay / total bookings"
            example="Room Beta had 120 bookings last month. 22 of them had no one detected within the 10-min no-show delay. No-show rate = 22 / 120 = 18.3%."
            note="Configurable via the 'No-show delay' setting. Shorter delays catch more ghost meetings."
            color="rose"
          />
          <KPIExplainer
            number="04"
            title="Optimization potential"
            formula="max(0, (1 − desk occupancy) − safety headroom)"
            example="If desk occupancy is 42.3%, idle share is 57.7%. With a 20% safety headroom for peak Tuesdays, optimization potential = 57.7% − 20% = 37.7% of capacity could be reclaimed."
            note="Drives the Insights savings: estimated savings = (potential × total desks) × cost per desk (set in Settings)."
            color="violet"
          />
        </div>
      </div>

      {/* Optional AI features */}
      <div className="mt-12" data-testid="ai-features">
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-heading text-2xl font-semibold tracking-tight text-slate-900">
              <BrainCircuit className="h-6 w-6 text-blue-600" />
              Optional AI capabilities
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Layer machine intelligence on top of the same anonymous presence
              data. All features are opt-in and can be deployed independently.
            </p>
          </div>
          <Badge variant="secondary" className="w-fit bg-blue-50 text-blue-700">
            Add-on modules
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <AIFeature
            icon={TrendingUp}
            title="Demand forecasting"
            body="Predict next week's occupancy by floor and department, factoring weekday, monthly seasonality and public holidays."
            badge="Forecast"
          />
          <AIFeature
            icon={AlertTriangle}
            title="Anomaly detection"
            body="Automatically flag unusual usage spikes or drops — e.g. an empty floor on a Tuesday — and route alerts to facilities."
            badge="Alerts"
          />
          <AIFeature
            icon={Wand2}
            title="Auto-rebalancing"
            body="Recommend zone or team reallocations to flatten peak demand and free up entire wings for sublet or refurbishment."
            badge="Optimization"
          />
          <AIFeature
            icon={Mail}
            title="Weekly insights digest"
            body="A natural-language summary delivered every Monday: what changed, what to do, and where the savings sit."
            badge="GenAI"
          />
          <AIFeature
            icon={MessageSquare}
            title="Conversational analytics"
            body={'Ask the data in plain English: "Which department wastes the most space?" or "Find ghost meetings on Floor 2."'}
            badge="Chat"
          />
          <AIFeature
            icon={Camera}
            title="Vision-based occupancy"
            body="Optional ceiling cameras with on-device anonymization for very dense or atypical zones (lobbies, cafeterias)."
            badge="Privacy-safe"
          />
        </div>
      </div>

      {/* Privacy reassurance */}
      <div
        className="mt-10 grid grid-cols-1 gap-6 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:grid-cols-2"
        data-testid="privacy-section"
      >
        <img
          src="https://images.pexels.com/photos/13068370/pexels-photo-13068370.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Sleek office interior"
          className="h-72 w-full object-cover md:h-full"
        />
        <div className="p-8">
          <span className="text-xs font-medium uppercase tracking-widest text-blue-600">
            Designed with privacy first
          </span>
          <h3 className="mt-2 font-heading text-2xl font-semibold text-slate-900">
            We measure spaces, not people.
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-slate-600">
            <li className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
              No cameras, no microphones, no badge readers.
            </li>
            <li className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
              Sensor data is aggregated to zone-level before reaching the
              dashboard.
            </li>
            <li className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
              Daily granularity by default; hourly available when relevant.
            </li>
            <li className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
              Compliant with GDPR Article 5 — purpose limitation & minimisation.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body, testId }) {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-50 text-blue-600">
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">
          {title}
        </h3>
        <p className="mt-2 text-sm text-slate-600">{body}</p>
      </CardContent>
    </Card>
  );
}

function Formula({ label, formula, description, icon: Icon }) {
  return (
    <div className="bg-white p-6">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        <Icon className="h-4 w-4 text-blue-600" />
        {label}
      </div>
      <div className="mt-3 rounded-md bg-slate-900 px-3 py-2 font-mono text-xs text-slate-100">
        {formula}
      </div>
      <p className="mt-3 text-sm text-slate-600">{description}</p>
    </div>
  );
}

function KPIExplainer({ number, title, formula, example, note, color }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    violet: "bg-violet-50 text-violet-700",
  };
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={`kpi-explainer-${number}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <span
            className={`inline-flex h-6 items-center rounded-md px-2 text-xs font-semibold tracking-wider ${
              tones[color] || tones.blue
            }`}
          >
            {number}
          </span>
          <span className="text-xs font-medium text-slate-400">KPI</span>
        </div>
        <h4 className="mt-3 font-heading text-lg font-semibold text-slate-900">
          {title}
        </h4>
        <div className="mt-3 rounded-md bg-slate-900 px-3 py-2 font-mono text-xs text-slate-100">
          {formula}
        </div>
        <p className="mt-3 text-sm text-slate-600">
          <span className="font-semibold text-slate-900">Example. </span>
          {example}
        </p>
        <p className="mt-2 text-xs text-slate-500">{note}</p>
      </CardContent>
    </Card>
  );
}

function AIFeature({ icon: Icon, title, body, badge }) {
  return (
    <Card
      className="group relative overflow-hidden border-slate-200 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
      data-testid={`ai-feature-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-blue-200 via-violet-200 to-transparent" />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gradient-to-br from-blue-50 to-violet-50 text-blue-600">
            <Icon className="h-5 w-5" />
          </div>
          <Badge variant="secondary" className="bg-slate-100 text-slate-600">
            {badge}
          </Badge>
        </div>
        <h4 className="mt-4 font-heading text-base font-semibold text-slate-900">
          {title}
        </h4>
        <p className="mt-2 text-sm text-slate-600">{body}</p>
        <div className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-blue-600">
          Optional module
          <ArrowRight className="h-3 w-3" />
        </div>
      </CardContent>
    </Card>
  );
}
